import { useStore } from 'zustand';

import { type Entity } from '@/domain/model';
import { ProjectionKind, SolidKind } from '@/domain/solid';
import { type EditorStore } from '@/store';

import { PanelSection } from './Inspector';
import { colour } from './theme';

/**
 * Solid parameters for the current selection.
 *
 * Appears only when a solid is selected, and vanishes otherwise. A panel permanently
 * showing controls for something the user has not selected is noise, and this one carries
 * controls that would be meaningless applied to a circle.
 *
 * ## Why projection is here and not a document setting
 *
 * Each solid carries its own. Putting a cube in isometric next to the same cube in cabinet
 * projection is a lesson about projection, and a single document-wide setting would make
 * that impossible to draw. The cost is that changing five solids means five changes — which
 * is what multi-select is for, and this panel writes to the whole selection at once.
 *
 * Fields the selection disagrees on show as unset rather than showing the first solid's
 * value, for the same reason the style panel does: a control that lies about what is
 * selected is how a user changes something they did not mean to.
 */

const PROJECTIONS: readonly { value: ProjectionKind; label: string; title: string }[] = [
  {
    value: ProjectionKind.Isometric,
    label: 'Iso',
    title: 'Isometric — all three axes equally foreshortened',
  },
  {
    value: ProjectionKind.Cabinet,
    label: 'Cab',
    title: 'Cabinet — front face true, depth halved at 45°',
  },
  { value: ProjectionKind.Perspective, label: 'Persp', title: 'Perspective — nearer edges larger' },
];

/** Solids whose base is a polygon the user can choose the sides of. */
const POLYGONAL = new Set<string>([SolidKind.Prism, SolidKind.Pyramid]);

/** An entity narrowed to one carrying a solid source. */
type SolidEntity = Entity & { readonly source: Extract<Entity['source'], { op: 'solid' }> };

export interface SolidPanelProps {
  readonly store: EditorStore;
}

export function SolidPanel({ store }: SolidPanelProps) {
  const document = useStore(store, (state) => state.document);
  const selection = useStore(store, (state) => state.selection);
  const commands = useStore(store, (state) => state.commands);

  // The type predicate is what lets the fields below read solid parameters without casts.
  const solids = [...selection]
    .map((id) => document.entities.get(id))
    .filter((entity): entity is SolidEntity => entity?.source.op === 'solid');

  const first = solids[0];
  if (first === undefined) return <FromBase store={store} />;

  const ids = solids.map((entity) => entity.id);

  /** A value the whole selection agrees on, or undefined when it does not. */
  const shared = <T,>(read: (source: SolidEntity['source']) => T): T | undefined => {
    const value = read(first.source);
    return solids.every((entity) => read(entity.source) === value) ? value : undefined;
  };

  const projection = shared((source) => source.projection);
  const width = shared((source) => source.width);
  const height = shared((source) => source.height);
  const sides = shared((source) => source.sides);
  const showsSides = solids.every((entity) => POLYGONAL.has(entity.source.solid));

  return (
    <PanelSection
      title="Khối không gian"
      {...(solids.length === 1 ? {} : { badge: String(solids.length) })}
    >
      <Field label="Phép chiếu">
        <div style={{ display: 'flex', gap: 2 }}>
          {PROJECTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              title={option.title}
              onClick={() => {
                commands.setSolid(ids, { projection: option.value });
              }}
              className={projection === option.value ? 'es-btn es-btn--on' : 'es-btn'}
              aria-pressed={projection === option.value}
              style={{ flex: 1, padding: '4px 2px', fontSize: 11 }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </Field>

      <Field label={`Chiều rộng${width === undefined ? '' : ` ${String(Math.round(width))}`}`}>
        <input
          type="range"
          min={20}
          max={400}
          step={5}
          value={width ?? 120}
          onChange={(event) => {
            const next = Number(event.target.value);
            // A cube stays a cube: changing one extent has to change all three, or the
            // control silently turns it into a cuboid that still calls itself a cube.
            const cubes = solids.filter((e) => e.source.solid === SolidKind.Cube).map((e) => e.id);
            const others = solids.filter((e) => e.source.solid !== SolidKind.Cube).map((e) => e.id);
            if (cubes.length > 0) {
              commands.setSolid(cubes, { width: next, height: next, depth: next });
            }
            if (others.length > 0) commands.setSolid(others, { width: next });
          }}
          style={{ width: '100%' }}
        />
      </Field>

      <Field label={`Chiều cao${height === undefined ? '' : ` ${String(Math.round(height))}`}`}>
        <input
          type="range"
          min={20}
          max={400}
          step={5}
          value={height ?? 120}
          onChange={(event) => {
            commands.setSolid(ids, { height: Number(event.target.value) });
          }}
          style={{ width: '100%' }}
        />
      </Field>

      {showsSides && (
        <Field label={`Số cạnh đáy${sides === undefined ? '' : ` ${String(sides)}`}`}>
          <input
            type="range"
            min={3}
            max={12}
            step={1}
            value={sides ?? 6}
            onChange={(event) => {
              commands.setSolid(ids, { sides: Number(event.target.value) });
            }}
            style={{ width: '100%' }}
          />
        </Field>
      )}

      <p style={{ margin: 0, color: colour.faint, fontSize: 10, lineHeight: 1.4 }}>
        Chọn <strong>Xoay khối</strong> rồi kéo để quay. Cạnh khuất luôn vẽ nét đứt.
      </p>
    </PanelSection>
  );
}

function Field({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ color: colour.muted, fontSize: 11 }}>{label}</span>
      {children}
    </label>
  );
}

/**
 * Building a solid on a shape the teacher just drew.
 *
 * ## Why it lives in this panel rather than in the toolbar
 *
 * Because it acts on a *selection*, like everything else in the rail, and because this is the
 * section somebody is looking at when the thought "I want a solid" occurs. A toolbar entry would
 * have to be a mode you enter and then click something in, which is a worse fit for a gesture
 * whose subject already exists.
 *
 * ## What it asks of the drawing
 *
 * The outline is read as the base's **image**: the solid is built so its floor projects back onto
 * exactly those points. So an outline drawn as a floor *looks* like — foreshortened, leaning the
 * way the figure leans — gives a solid that stands on it, and one drawn face-on gives a body seen
 * face-on, which is a flat-looking figure and a fair answer to what was asked.
 */
function FromBase({ store }: { readonly store: EditorStore }) {
  const document = useStore(store, (state) => state.document);
  const selection = useStore(store, (state) => state.selection);
  const commands = useStore(store, (state) => state.commands);

  const chosen = [...selection]
    .map((id) => document.entities.get(id))
    .filter((entity) => entity?.source.op === 'polygon');

  const polygon = chosen.length === 1 ? chosen[0] : undefined;
  if (polygon === undefined) return null;

  return (
    <PanelSection title="Khối không gian">
      <span className="es-eyebrow">Dựng khối trên đáy này</span>
      <div style={{ display: 'flex', gap: 4 }}>
        {(
          [
            ['Lăng trụ', 'prism'],
            ['Hình chóp', 'pyramid'],
          ] as const
        ).map(([label, kind]) => (
          <button
            key={kind}
            type="button"
            onClick={() => {
              commands.solidFromPolygon(polygon.id, kind);
            }}
            className="es-btn"
            style={{ flex: 1, padding: '6px 0', fontSize: 11 }}
          >
            {label}
          </button>
        ))}
      </div>
      <p style={{ margin: 0, color: colour.faint, fontSize: 10, lineHeight: 1.45 }}>
        Đáy giữ nguyên chỗ bạn đã vẽ, các đỉnh được đặt tên A, B, C… Đa giác gốc được ẩn đi chứ
        không xoá — mở lại trong bảng Đối tượng nếu cần.
      </p>
    </PanelSection>
  );
}
